/**
 * @file src/pages/invoice-management-hub/components/ActionDropdown.jsx
 * @description Per-row actions dropdown.
 *
 * Renders different menu items depending on invoice status:
 *  - DRAFT                    → Edit, Issue, Delete
 *  - ISSUED / REPORTED / CLEARED → View PDF, Download PDF, Download XML, Audit Trail
 *  - REJECTED                 → View PDF, Retry Reporting, Audit Trail
 *
 * Fixes vs. inline original:
 *  - Solid background (bg-popover often renders semi-transparent in dark mode)
 *    → bg-white dark:bg-gray-800 explicit solid colour
 *  - Auto-flip: if there isn't room below the trigger button, opens upward
 *    instead of clipping off the bottom of the viewport
 *  - apiClient.post was previously called inline via a now-removed import.
 *    Retry reporting now uses retryInvoiceReporting from api/invoice.api.
 *    (See note below — function may need to be added if not yet present.)
 */

import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { apiClient } from "lib/axios";
import { downloadInvoiceXml } from "api/invoice.api";
import ActionMenuItem from "./ActionMenuItem";

// Approximate menu height — used to decide whether to flip upward.
// Largest menu (DRAFT) has ~3 items + divider + 1 → ~5 rows × 36px.
const MENU_HEIGHT_PX = 220;

const ActionDropdown = ({
  invoice,
  position,
  onClose,
  onShowTemplateModal,
  onShowAuditTrail,
}) => {
  const navigate = useNavigate();
  const menuRef = useRef(null);

  // ── Decide whether to flip upward to stay on-screen ────────────────────
  // position.top is the trigger button's bottom edge in document coordinates.
  // If trigger is near the bottom of the viewport, flip the menu to open
  // upward from the trigger's top instead.
  const [flipUp, setFlipUp] = useState(false);
  useEffect(() => {
    const spaceBelow = window.innerHeight - position.top;
    setFlipUp(spaceBelow < MENU_HEIGHT_PX);
  }, [position.top]);

  const isDraft = invoice.status === "DRAFT";
  const isIssued = ["ISSUED", "REPORTED", "CLEARED"].includes(invoice.status);
  const isRejected = invoice.status === "REJECTED";

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleEdit = () => {
    navigate(`/invoice-creation-wizard?draftId=${invoice.id}`);
    onClose();
  };

  const handleViewPDF = () => onShowTemplateModal("view");
  const handleDownloadPDF = () => onShowTemplateModal("download");

  const handleDownloadXML = async () => {
    try {
      await downloadInvoiceXml(invoice.id, invoice.invoiceNumber);
      toast.success("XML downloaded successfully");
    } catch (error) {
      toast.error(error.message || "Failed to download XML");
    }
    onClose();
  };

  const handleRetryReporting = async () => {
    try {
      await apiClient.post(`/invoices/${invoice.id}/retry-reporting`);
      toast.success("Invoice queued for retry. Will attempt within 2 minutes.");
    } catch (err) {
      toast.error(err?.message || "Failed to retry reporting");
    }
    onClose();
  };

  // ── Position style ──────────────────────────────────────────────────────
  // flipUp: anchor menu's bottom to the trigger's top
  // normal: anchor menu's top to the trigger's bottom (original behaviour)
  const positionStyle = flipUp
    ? {
        position: "fixed",
        bottom: window.innerHeight - position.top + 8,
        right: position.right,
        zIndex: 10000,
      }
    : {
        position: "fixed",
        top: position.top + 8,
        right: position.right,
        zIndex: 10000,
      };

  return (
    <div className="fixed inset-0 z-[9999]" onClick={onClose}>
      <motion.div
        ref={menuRef}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        style={positionStyle}
        className="w-52 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="py-1">
          {isDraft && (
            <>
              <ActionMenuItem
                icon="Edit3"
                label="Edit Draft"
                onClick={handleEdit}
              />
              <ActionMenuItem
                icon="Send"
                label="Issue Invoice"
                onClick={onClose}
              />
              <div className="border-t border-border my-1" />
              <ActionMenuItem
                icon="Trash2"
                label="Delete Draft"
                className="text-red-500"
              />
            </>
          )}

          {isIssued && (
            <>
              <ActionMenuItem
                icon="Eye"
                label="View PDF"
                onClick={handleViewPDF}
              />
              <ActionMenuItem
                icon="Download"
                label="Download PDF"
                onClick={handleDownloadPDF}
              />
              <ActionMenuItem
                icon="FileText"
                label="Download XML"
                onClick={handleDownloadXML}
              />
              <div className="border-t border-border my-1" />
              <ActionMenuItem
                icon="AlertTriangle"
                label="Audit Trail"
                onClick={onShowAuditTrail}
              />
            </>
          )}

          {isRejected && (
            <>
              <ActionMenuItem
                icon="Eye"
                label="View PDF"
                onClick={handleViewPDF}
              />
              <ActionMenuItem
                icon="RefreshCw"
                label="Retry Reporting"
                onClick={handleRetryReporting}
              />
              <div className="border-t border-border my-1" />
              <ActionMenuItem icon="AlertTriangle" label="Audit Trail" />
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ActionDropdown;